export type RoutingGraphImportRunStatus =
  | 'queued'
  | 'running'
  | 'promoting'
  | 'succeeded'
  | 'failed'
  | 'cancel_requested'
  | 'cancelled'
  | 'stalled';

export interface RoutingGraphImportCounters {
  processed?: number;
  expected?: number;
  road_edges?: number;
  rail_edges?: number;
}

export interface RoutingGraphImportCapacity {
  [key: string]: unknown;
  accepted?: boolean;
  reason?: string;
  required_bytes?: number;
  available_bytes?: number;
  reserved_bytes?: number;
}

export interface RoutingGraphImportRun {
  id: string;
  region_key?: string;
  status?: RoutingGraphImportRunStatus | string;
  phase?: string;
  progress_percent?: number;
  progress_message?: string;
  queued_at?: string;
  started_at?: string;
  finished_at?: string;
  phase_started_at?: string;
  phase_elapsed_seconds?: number;
  attempt?: number;
  owner?: string;
  worker_id?: string;
  lease_expires_at?: string;
  last_heartbeat_at?: string;
  heartbeat_at?: string;
  stalled?: boolean;
  cancellation_requested_at?: string;
  cancel_requested?: boolean;
  cancel_requested_at?: string;
  cancel_requested_by?: string;
  cancel_reason?: string;
  cancelled_at?: string;
  processed_count?: number;
  expected_count?: number;
  throughput_per_second?: number;
  queue_age_seconds?: number;
  counters?: RoutingGraphImportCounters;
  capacity?: RoutingGraphImportCapacity;
  segments_seen?: number;
  road_segments_seen?: number;
  rail_segments_seen?: number;
  nodes_seen?: number;
  nodes_loaded?: number;
  current_batch?: number;
  total_batches?: number;
  rows_per_second?: number;
  heartbeat_age_seconds?: number;
  last_progress_at?: string;
  estimated_remaining_seconds?: number;
  cleanup_phase?: string;
  error?: string;
  graph_version?: string;
  serving_active?: boolean;
  activated_at?: string;
}

export interface RoutingGraphServingMember {
  region_key: string;
  graph_version?: string;
  artifact_checksum?: string;
}

export interface RoutingGraphServingStatus {
  graph_version?: string;
  region_keys?: string[];
  promoted_at?: string;
  active?: boolean;
  member_graph_versions?: string[];
  storage_mode?: string;
  composition?: 'direct_alias' | 'bounded_seams' | string;
  members?: RoutingGraphServingMember[];
}

export interface RoutingGraphRegionStatus {
  region_key: string;
  label?: string;
  last_run?: RoutingGraphImportRun | null;
}

export interface RoutingGraphImportStatus {
  regions?: RoutingGraphRegionStatus[];
  serving_graph?: RoutingGraphServingStatus | null;
  recent_runs?: RoutingGraphImportRun[];
}

type RoutingGraphImportStatusEnvelope = { data?: RoutingGraphImportStatus };

export class RoutingGraphImportStatusError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = 'RoutingGraphImportStatusError';
  }
}

export async function getRoutingGraphImportStatus(): Promise<RoutingGraphImportStatus> {
  const response = await fetch('/api/v1/routing/admin/osm/status', {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    let message = `Routing graph status request failed (${response.status}).`;
    try {
      const body = await response.json() as { error?: string; message?: string };
      message = body.message || body.error || message;
    } catch {
      // Preserve the safe status-based message for non-JSON failures.
    }
    throw new RoutingGraphImportStatusError(response.status, message);
  }
  const payload = await response.json() as RoutingGraphImportStatus | RoutingGraphImportStatusEnvelope;
  return 'data' in payload ? payload.data ?? {} : payload;
}
